import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { find, isEmpty } from 'lodash';
import {
  arrayOf, bool, func, number, shape, string,
} from 'prop-types';
import {
  Column,
  Container,
  Grid,
  RightPanel,
  RightBarContainer,
  ContainerHeader,
  QuestionOrder,
  Title,
} from './styles';

import { PROCESSED_STATUS } from '../../participants-video/video-features/constants';
import QuestionVideoTranscriptPlaylist from '../question-video-answer-transcript-playlist';
import getQuestionAnswer from '../../../../core/utils/get-question-answer';
import PlaylistPlayerHud from '../../../../core/components/video-player/playlist-player-hud';
import renderComponentToHTML from '../../participants-video/video-features/marker-unmoderated';
import {
  deleteVerticalLine,
  getPositions,
  positionVerticalLine,
} from '../../participants-video/video-features/marker-helper/marker-helper';
import { config } from '../../../../config';

const QuestionVideoFeatures = (props) => {
  const {
    testers,
    project,
    testersLoading,
    isAdmin,
  } = props;

  const { questionId } = useParams();

  const { surveys } = project;

  const question = useMemo(() => {
    const sessionSurvey = find(surveys, { surveyType: 'session' });
    if (!sessionSurvey) {
      return undefined;
    }

    const { questions } = sessionSurvey;

    return find(questions, { id: questionId });
  }, [questionId, surveys]);

  const testSessions = useMemo(() => {
    const sessionsWithUser = [];
    testers.forEach((tester) => {
      const session = tester.testSessions.filter((ts) => ts.status === PROCESSED_STATUS && ts.removed === false)[0] || {};
      sessionsWithUser.push({
        ...session,
        user: tester.user,
      });
    });

    return sessionsWithUser;
  }, [testers]);

  const transcribedAnswersPlaylist = useMemo(() => {
    const answers = [];
    testSessions.forEach((testSession) => {
      const {
        testSessionAnswers, user, videoUrl, id,
      } = testSession;

      answers.push({
        user,
        answer: getQuestionAnswer(testSessionAnswers, question && question.id),
        videoUrl,
        videoId: id,
      });
    });

    const filteredAnswers = answers.filter((answer) => answer.answer && question);

    return filteredAnswers.map((answer, index) => ({ ...answer, index }));
  }, [question, testSessions]);

  const testSession = useMemo(() => {
    const filteredSessions = testSessions.filter((session) => {
      const keys = Object.keys(session);
      return keys.length > 1 || (keys.length === 1 && keys[0] !== 'user');
    });
    if (isEmpty(filteredSessions)) {
      return {};
    }

    // when it is admin, we show the video, companies cant see the video if is removed
    if (isAdmin) {
      //  We need to validate if the project is a practice project to get the right test session
      //  Because it can have multiple sessions, we need to get the approved/submitted test session if it exists
      //  If it is not a practice project, we need to get the first test session
      if (project.id === config.REACT_APP_PRACTICE_TEST_PROJECT_ID) {
        const sessionApproved = find(filteredSessions, (session) => session.practiceTestStatus === 'approved');

        if (sessionApproved) return sessionApproved;

        const sessionSubmitted = find(filteredSessions, (session) => session.practiceTestStatus === 'submitted');

        if (sessionSubmitted) return sessionSubmitted;
      }

      return filteredSessions || {};
    }

    return filteredSessions
      .filter((ts) => ts.status === PROCESSED_STATUS && ts.removed === false)[0] || {};
  }, [isAdmin, project.id, testSessions]);

  const getTranscripts = (testSessionTranscript) => {
    if (isAdmin) {
      if (isEmpty(testSessionTranscript[0]?.testSessionAnswers)) {
        return testSessionTranscript[0]?.testSessionSpeakers;
      }
      return testSessionTranscript[0]?.testSessionAnswers;
    }
    return testSessionTranscript?.testSessionAnswers;
  };

  const transcripts = getTranscripts(testSession);

  const buildMarkers = transcripts?.map((answer) => ({
    time: answer.videoStartTime,
    html: renderComponentToHTML({
      questionType: answer.question?.questionType,
      videoStartTime: answer.videoStartTime,
      label: answer.question?.label,
      isMarker: true,
    }),
    isMarker: true,
  }));

  const markers = {
    markerStyle: {
      width: '10px',
      height: '10px',
      'background-color': 'white',
      'border-radius': '50%',
      cursor: 'pointer',
      position: 'absolute',
      top: '-12px',
      display: project.testType === 'moderated' ? 'none' : 'block',
    },
    markers: buildMarkers,
    markerTip: {
      html(marker) {
        return marker.html;
      },
    },
    onMarkerReached: (marker) => {
      let positions = null;
      let parentElement = null;
      const result = getPositions(marker);
      if (result) {
        ({ positions, parentElement } = result);
      }
      deleteVerticalLine();
      // this is a gambiarra for performance, but it works so well.
      if (!marker.isMarker) {
        positionVerticalLine(positions, parentElement);
      }
    },
  };

  return (
    <>
      <Container>
        <Grid columns={2} stackable>
          <Column width={11}>
            <PlaylistPlayerHud
              autoplay
              videos={transcribedAnswersPlaylist}
              url={`/projects/${project.id}/videos/questions/${question.id}`}
              markers={markers}
            />
          </Column>
          <Column width={5}>
            <RightPanel>
              <RightBarContainer>
                <ContainerHeader>
                  <QuestionOrder>
                    Question
                    {' '}
                    {question.questionOrder + 1}
                    {': '}
                  </QuestionOrder>

                  <Title>{question.label}</Title>
                </ContainerHeader>

                <QuestionVideoTranscriptPlaylist
                  transcribedAnswers={transcribedAnswersPlaylist}
                  loading={testersLoading}
                  question={question}
                />
              </RightBarContainer>
            </RightPanel>
          </Column>
        </Grid>
      </Container>
    </>
  );
};

QuestionVideoFeatures.propTypes = {
  testers: arrayOf(shape({
    id: string.isRequired,
    testSessions: arrayOf(shape({
      id: string.isRequired,
      videoUrl: string.isRequired,
      testSessionAnswers: arrayOf(shape({})).isRequired,
      ratingDescription: string,
      ratingPrivate: bool,
      rating: number,
      filter: func,
    })),
  })),
  project: shape({
    id: string.isRequired,
    testType: string.isRequired,
    surveys: arrayOf(shape({})).isRequired,
    company: shape({
      id: string.isRequired,
    }).isRequired,
    faceRecording: bool.isRequired,
  }).isRequired,
  testersLoading: bool,
  isAdmin: bool.isRequired,
};

QuestionVideoFeatures.defaultProps = {
  testers: [],
  testersLoading: true,
};

const mapStateToProps = ({ authentication }) => ({
  isAdmin: authentication?.user?.admin || false,
});

export default connect(mapStateToProps, null)(QuestionVideoFeatures);
